// Domain · SideChip
//
// Factory-bound chip for the cost-axis `side` enum used by price lists,
// transactions, transports, AND invoice cost categories. Tri-valued —
// `"load" | "unload" | "transport"`.
//
// Tones: load → blue, unload → purple, transport → grey. The grey for
// transport keeps the chip in the 5-tone budget (load/unload already
// claim two distinct hues) and matches the muted-text fallback that
// reconcile pickers previously inlined.
//
// Labels are pulled from i18next:
//   - load / unload / transport → `price_lists.side.{load|unload|transport}`
//
// Exports `sideTone()` / `sideText()` so callers that need only the tone
// or label (e.g. an Alert accent, a StatBand sub-line) don't have to
// render the chip.

import type { PillTone } from "@ds/display/Pill";
import { SIDE_TONES, SIDE_TEXT, SIDE_TR_KEY, type Side } from "@ds/domain/SideChip.helpers";

export type { Side } from "@ds/domain/SideChip.helpers";
import { StatusChipShell } from "@ds/display/StatusChipShell";
import { useTranslation } from "react-i18next";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface SideChipInput {
  /** Side enum value. `null` / `undefined` → em-dash. */
  side: Side | null | undefined;
  /** Rendering variant. Default `"chip"`. */
  variant?: "chip" | "inline";
  /** Pill size — only meaningful when `variant="chip"`. Default `"md"`. */
  size?: "sm" | "md";
  /** `data-testid` on the root. Defaults to `"side-chip"`. */
  testId?: string;
}

export type SideChipProps = SideChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function SideChip({
  side,
  variant = "chip",
  size = "md",
  testId = "side-chip",
}: SideChipProps) {
  const { t } = useTranslation();
  const tone: PillTone | null = side ? SIDE_TONES[side] ?? "grey" : null;
  const label: string | null  = side ? t(SIDE_TR_KEY[side], SIDE_TEXT[side]) : null;
  return (
    <StatusChipShell
      tone={tone}
      label={label}
      variant={variant}
      size={size}
      testId={testId}
    />
  );
}
